import type { BoqRow, DiffSummary, MatchResult } from '../../types'

function uniqueRows(results: readonly MatchResult[], side: 'baselineRow' | 'targetRow'): BoqRow[] {
  const rows = new Map<string, BoqRow>()
  for (const result of results) {
    const row = result[side]
    if (row !== null) rows.set(row.id, row)
  }
  return [...rows.values()]
}

function sumKnownTotals(rows: readonly BoqRow[]): number {
  return rows.reduce((sum, row) => sum + (row.effectiveTotalPrice ?? 0), 0)
}

export function summarizeDiffResults(results: readonly MatchResult[]): DiffSummary {
  const baselineRows = uniqueRows(results, 'baselineRow')
  const targetRows = uniqueRows(results, 'targetRow')
  const count = (status: MatchResult['status']): number =>
    results.reduce((total, result) => total + (result.status === status ? 1 : 0), 0)

  return {
    baselineItemCount: baselineRows.length,
    targetItemCount: targetRows.length,
    addedCount: count('added'),
    removedCount: count('removed'),
    changedCount: count('changed'),
    unchangedCount: count('unchanged'),
    pendingCount: count('pending'),
    baselineTotal: sumKnownTotals(baselineRows),
    targetTotal: sumKnownTotals(targetRows),
    baselineMissingTotalCount: baselineRows.filter((row) => row.effectiveTotalPrice === null)
      .length,
    targetMissingTotalCount: targetRows.filter((row) => row.effectiveTotalPrice === null).length,
    confirmedNetAmountImpact: results.reduce(
      (sum, result) =>
        sum +
        (result.amountImpact.includedInConfirmedTotal && result.amountImpact.value !== null
          ? result.amountImpact.value
          : 0),
      0,
    ),
    pendingAmountImpact: results.reduce(
      (sum, result) =>
        sum +
        (result.amountImpact.state === 'pending' && result.amountImpact.value !== null
          ? result.amountImpact.value
          : 0),
      0,
    ),
    unavailableAmountImpactCount: results.filter(
      (result) => result.amountImpact.state === 'unavailable',
    ).length,
  }
}

export const summarizeDiff = summarizeDiffResults
